// import { useFormContext } from 'react-hook-form';
// import { useCompleteTicket } from '@/hooks/api/useTickets';
// import { usePermissions } from '@/hooks/usePermissions';
// import { toast } from 'sonner';
// import { CheckCircle2, ClipboardCheck, Loader2, Lock } from 'lucide-react';
// import { cn } from '@/lib/utils';
// import { TicketStatus } from '@/types/enums';

// const CHECKLIST_ITEMS = [
//   {
//     id: 'powerRestored',
//     label: 'Fiber has been restored to all affected customers',
//   },
//   { id: 'safetyAddressed', label: 'All safety hazards have been addressed' },
//   {
//     id: 'equipmentTested',
//     label: 'Equipment has been tested and is functioning properly',
//   },
//   { id: 'siteRestored', label: 'Site has been cleaned and restored' },
// ];

// interface TicketDetailsCompletionTabProps {
//   ticket: any;
//   permissions: ReturnType<typeof usePermissions>;
//   onSuccess?: () => void;
// }

// export const TicketDetailsCompletionTab = ({
//   ticket,
//   permissions,
//   onSuccess,
// }: TicketDetailsCompletionTabProps) => {
//   const { register, watch, handleSubmit } = useFormContext();
//   const { completeTicket, isCompleting } = useCompleteTicket();

//   const checklist = watch('completionChecklist') ?? {};
//   const resolutionSummary = watch('resolutionSummary') ?? '';

//   const allChecked = CHECKLIST_ITEMS.every((item) => checklist[item.id]);
//   const alreadyDone = ticket?.status === TicketStatus.COMPLETED;
//   const canSubmit =
//     permissions.canEditTicket &&
//     allChecked &&
//     resolutionSummary.trim().length > 0 &&
//     !alreadyDone;

//   // ── Already completed ───────────────────────────────────────────────────
//   if (alreadyDone) {
//     return (
//       <div className="pt-4 space-y-4">
//         <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
//           <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
//           <div>
//             <p className="text-sm font-semibold text-foreground">
//               Ticket Completed
//             </p>
//             <p className="text-xs text-muted-foreground mt-0.5">
//               This ticket has been marked complete. AI review is in progress.
//             </p>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // ── No edit rights ───────────────────────────────────────────────────────
//   if (!permissions.canEditTicket) {
//     return (
//       <div className="pt-4">
//         <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/40 border border-border">
//           <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
//           <p className="text-sm text-muted-foreground">
//             {permissions.iAmAssigned
//               ? 'Only the active editor can complete this ticket.'
//               : 'Claim this ticket to mark it as complete.'}
//           </p>
//         </div>
//       </div>
//     );
//   }

//   const onSubmit = (data: any) => {
//     completeTicket(
//       {
//         ticketId: ticket.id,
//         resolutionSummary: data.resolutionSummary,
//         completionChecklist: data.completionChecklist,
//       },
//       {
//         onSuccess: () => {
//           toast.success('Ticket marked as complete. AI review started.');
//           onSuccess?.();
//         },
//         onError: () => {
//           toast.error('Failed to complete ticket.');
//         },
//       },
//     );
//   };

//   return (
//     <div className="pt-4 space-y-5">
//       {/* CHECKLIST */}
//       <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
//         <div className="flex items-center gap-2">
//           <ClipboardCheck className="w-4 h-4 text-primary" />
//           <h3 className="text-sm font-semibold text-foreground">
//             Completion Checklist
//           </h3>
//           <span className="ml-auto text-xs text-muted-foreground">
//             {CHECKLIST_ITEMS.filter((i) => checklist[i.id]).length} /{' '}
//             {CHECKLIST_ITEMS.length}
//           </span>
//         </div>

//         <div className="space-y-2.5">
//           {CHECKLIST_ITEMS.map((item) => {
//             const checked = !!checklist[item.id];
//             return (
//               <label
//                 key={item.id}
//                 className={cn(
//                   'flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors',
//                   checked
//                     ? 'bg-green-500/10 border-green-500/20'
//                     : 'bg-card border-border hover:bg-muted/30',
//                 )}
//               >
//                 <input
//                   type="checkbox"
//                   className="h-4 w-4 accent-primary"
//                   {...register(`completionChecklist.${item.id}`)}
//                 />
//                 <span
//                   className={cn(
//                     'text-sm transition-colors',
//                     checked ? 'text-foreground' : 'text-muted-foreground',
//                   )}
//                 >
//                   {item.label}
//                 </span>
//                 {checked && (
//                   <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto shrink-0" />
//                 )}
//               </label>
//             );
//           })}
//         </div>
//       </div>

//       {/* RESOLUTION SUMMARY */}
//       <div className="space-y-2">
//         <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
//           Resolution Summary <span className="text-destructive">*</span>
//         </label>
//         <textarea
//           {...register('resolutionSummary')}
//           rows={4}
//           placeholder="Describe what was done to resolve this ticket..."
//           className={cn(
//             'w-full rounded-lg border border-border bg-background px-3 py-2.5',
//             'text-sm text-foreground placeholder:text-muted-foreground',
//             'resize-none focus:outline-none focus:ring-1 focus:ring-primary',
//             'transition-colors',
//           )}
//         />
//         {resolutionSummary.trim().length === 0 && (
//           <p className="text-[11px] text-muted-foreground">
//             Required before submitting.
//           </p>
//         )}
//       </div>

//       {/* SUBMIT */}
//       <button
//         type="button"
//         disabled={!canSubmit || isCompleting}
//         onClick={() => handleSubmit(onSubmit)()}
//         className={cn(
//           'w-full h-10 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2',
//           canSubmit && !isCompleting
//             ? 'bg-green-600 hover:bg-green-700 text-white'
//             : 'bg-muted text-muted-foreground cursor-not-allowed',
//         )}
//       >
//         {isCompleting ? (
//           <>
//             <Loader2 className="w-4 h-4 animate-spin" /> Completing...
//           </>
//         ) : (
//           <>
//             <CheckCircle2 className="w-4 h-4" /> Mark as Complete
//           </>
//         )}
//       </button>

//       {!allChecked && (
//         <p className="text-[11px] text-center text-muted-foreground">
//           Complete all checklist items before submitting.
//         </p>
//       )}
//     </div>
//   );
// };
import { useFormContext } from 'react-hook-form';
import { useCompleteTicket } from '@/hooks/api/useTickets';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  Lock,
  MessageSquareWarning,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TicketStatus } from '@packages/database/types';

const CHECKLIST_ITEMS = [
  {
    id: 'powerRestored',
    label: 'Fiber has been restored to all affected customers',
  },
  { id: 'safetyAddressed', label: 'All safety hazards have been addressed' },
  {
    id: 'equipmentTested',
    label: 'Equipment has been tested and is functioning properly',
  },
  { id: 'siteRestored', label: 'Site has been cleaned and restored' },
];

interface TicketDetailsCompletionTabProps {
  ticket: any;
  permissions: ReturnType<typeof usePermissions>;
  onSuccess?: () => void;
}
// ─── Rejection feedback banner ────────────────────────────────────────────────

const RejectionBanner = ({ notes }: { notes: string }) => (
  <div className="rounded-xl border border-destructive/30 bg-destructive/5 overflow-hidden">
    {/* Header strip */}
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-destructive/20 bg-destructive/10">
      <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />
      <p className="text-xs font-semibold text-destructive">
        Ticket returned — action required
      </p>
    </div>

    {/* Notes body */}
    <div className="px-4 py-3 flex items-start gap-2.5">
      <MessageSquareWarning className="w-4 h-4 text-destructive/70 shrink-0 mt-0.5" />
      <div className="space-y-1 min-w-0">
        <p className="text-xs font-medium text-muted-foreground">
          Admin feedback
        </p>
        <p className="text-sm text-foreground leading-relaxed">{notes}</p>
      </div>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const TicketDetailsCompletionTab = ({
  ticket,
  permissions,
  onSuccess,
}: TicketDetailsCompletionTabProps) => {
  const { register, watch, handleSubmit } = useFormContext();
  const { completeTicket, isCompleting } = useCompleteTicket();

  const checklist = watch('completionChecklist') ?? {};
  const resolutionSummary = watch('resolutionSummary') ?? '';

  const allChecked = CHECKLIST_ITEMS.every((item) => checklist[item.id]);
  const alreadyDone = ticket?.status === TicketStatus.FIELD_COMPLETE;
  const isRejected = ticket?.status === TicketStatus.REJECTED;
  const hasRejectionNotes = isRejected && !!ticket?.adminReviewNotes?.trim();

  const canSubmit =
    permissions.canEditTicket &&
    allChecked &&
    resolutionSummary.trim().length > 0 &&
    !alreadyDone;

  // ── Already completed ───────────────────────────────────────────────────
  if (alreadyDone) {
    return (
      <div className="pt-4 space-y-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-foreground">
              Ticket Completed
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              This ticket has been marked complete. AI review is in progress.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── No edit rights ───────────────────────────────────────────────────────
  if (!permissions.canEditTicket) {
    return (
      <div className="pt-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/40 border border-border">
          <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
          <p className="text-sm text-muted-foreground">
            {permissions.iAmAssigned
              ? 'Only the active editor can complete this ticket.'
              : 'Claim this ticket to mark it as complete.'}
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = (data: any) => {
    completeTicket(
      {
        ticketId: ticket.id,
        resolutionSummary: data.resolutionSummary,
        completionChecklist: data.completionChecklist,
      },
      {
        onSuccess: () => {
          toast.success('Ticket marked as complete. AI review started.');
          onSuccess?.();
        },
        onError: () => {
          toast.error('Failed to complete ticket.');
        },
      },
    );
  };

  return (
    <div className="pt-4 space-y-5">
      {/* ── Rejection feedback (only when returned by admin) ─────────────── */}
      {hasRejectionNotes && <RejectionBanner notes={ticket.adminReviewNotes} />}

      {/* CHECKLIST */}
      <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">
            Completion Checklist
          </h3>
          <span className="ml-auto text-xs text-muted-foreground">
            {CHECKLIST_ITEMS.filter((i) => checklist[i.id]).length} /{' '}
            {CHECKLIST_ITEMS.length}
          </span>
        </div>

        <div className="space-y-2.5">
          {CHECKLIST_ITEMS.map((item) => {
            const checked = !!checklist[item.id];
            return (
              <label
                key={item.id}
                className={cn(
                  'flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors',
                  checked
                    ? 'bg-green-500/10 border-green-500/20'
                    : 'bg-card border-border hover:bg-muted/30',
                )}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  {...register(`completionChecklist.${item.id}`)}
                />
                <span
                  className={cn(
                    'text-sm transition-colors',
                    checked ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {item.label}
                </span>
                {checked && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto shrink-0" />
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* RESOLUTION SUMMARY */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Resolution Summary <span className="text-destructive">*</span>
        </label>
        <textarea
          {...register('resolutionSummary')}
          rows={4}
          placeholder={
            isRejected
              ? 'Describe what was done to address the admin feedback…'
              : 'Describe what was done to resolve this ticket…'
          }
          className={cn(
            'w-full rounded-lg border bg-background px-3 py-2.5',
            'text-sm text-foreground placeholder:text-muted-foreground',
            'resize-none focus:outline-none focus:ring-1 transition-colors',
            isRejected
              ? 'border-destructive/40 focus:ring-destructive'
              : 'border-border focus:ring-primary',
          )}
        />
        {resolutionSummary.trim().length === 0 && (
          <p className="text-[11px] text-muted-foreground">
            Required before submitting.
          </p>
        )}
      </div>

      {/* SUBMIT */}
      <button
        type="button"
        disabled={!canSubmit || isCompleting}
        onClick={() => handleSubmit(onSubmit)()}
        className={cn(
          'w-full h-10 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2',
          canSubmit && !isCompleting
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-muted text-muted-foreground cursor-not-allowed',
        )}
      >
        {isCompleting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Completing...
          </>
        ) : (
          <>
            <CheckCircle2 className="w-4 h-4" />
            {isRejected ? 'Resubmit for review' : 'Mark as Complete'}
          </>
        )}
      </button>

      {!allChecked && (
        <p className="text-[11px] text-center text-muted-foreground">
          Complete all checklist items before submitting.
        </p>
      )}
    </div>
  );
};
